/** Web Audio helpers for MP3 playback after async synthesis (preserves user-gesture unlock). */

export function getAudioContextClass(): typeof AudioContext | null {
  if (typeof window === "undefined") return null;
  return (
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext ||
    null
  );
}

/** Call synchronously inside a click handler before any await. */
export function unlockAudioContext(context: AudioContext | null): void {
  if (!context) return;
  if (context.state === "suspended") {
    void context.resume();
  }
}

export function createSharedAudioContext(
  existing: AudioContext | null,
): AudioContext | null {
  const AudioCtx = getAudioContextClass();
  if (!AudioCtx) return null;
  const context = existing ?? new AudioCtx();
  unlockAudioContext(context);
  return context;
}

export type WebAudioPlaybackSession = {
  analyser: AnalyserNode;
  stop: () => void;
  done: Promise<void>;
};

export async function playMp3BlobWithAnalyser(
  blob: Blob,
  context: AudioContext,
): Promise<WebAudioPlaybackSession> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0));

  const analyser = context.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.94;

  const source = context.createBufferSource();
  source.buffer = audioBuffer;
  source.connect(analyser);
  analyser.connect(context.destination);

  let stopPlayback: (() => void) | null = null;
  let settled = false;

  const done = new Promise<void>((resolve, reject) => {
    const finish = (handler: () => void) => {
      if (settled) return;
      settled = true;
      handler();
    };

    source.onended = () => {
      finish(resolve);
    };

    stopPlayback = () => {
      finish(() => {
        try {
          source.stop();
        } catch {
          // Already stopped or never started.
        }
        reject(new Error("Playback stopped."));
      });
    };

    try {
      source.start(0);
    } catch (err) {
      finish(() =>
        reject(err instanceof Error ? err : new Error("Failed to play audio.")),
      );
    }
  });

  return {
    analyser,
    stop: () => stopPlayback?.(),
    done,
  };
}

export function disposeAnalyserNode(analyser: AnalyserNode | null): void {
  if (!analyser) return;
  try {
    analyser.disconnect();
  } catch {
    // Already disconnected.
  }
}
